/* global __firebase_config, __app_id, __initial_auth_token */
import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
// In your local environment, you'll need to install these dependencies by running:
// npm install firebase framer-motion
// For local development, ensure you have a React environment with Tailwind CSS configured.
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { getFirestore, doc, setDoc, updateDoc, collection, onSnapshot, writeBatch, deleteDoc, increment, serverTimestamp, getDocs } from 'firebase/firestore';
import { motion, useMotionValue, useTransform, useAnimation, AnimatePresence } from 'framer-motion';


// --- Helper: Icon Components ---
const BookmarkIcon = ({ className, filled }) => (
    <svg className={className} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
);
const SettingsIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
);
const TrophyIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path></svg>
);
const FlameIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path></svg>
);
const ShareIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path><polyline points="16 6 12 2 8 6"></polyline><line x1="12" y1="2" x2="12" y2="15"></line></svg>
);

// --- App Constants ---
const PREFERENCES_DOC_ID = 'userPreferences';
const APP_ID = typeof __app_id !== 'undefined' ? __app_id : 'default-joke-app-local';

// --- Helper Functions ---
const triggerHapticFeedback = () => {
    if (navigator.vibrate) navigator.vibrate(50);
};

function parseCSV(csvText) {
    const lines = csvText.trim().split('\n');
    lines.shift(); // Remove header row
    return lines.map(line => {
        line = line.trim();
        if (!line) return null;
        try {
            const lastComma = line.lastIndexOf(',');
            const comedian = line.substring(lastComma + 1).trim();
            const secondLastComma = line.lastIndexOf(',', lastComma - 1);
            const topic = line.substring(secondLastComma + 1, lastComma).trim();
            const firstComma = line.indexOf(',');
            const id = parseInt(line.substring(0, firstComma), 10);
            let text = line.substring(firstComma + 1, secondLastComma).trim();
            if (text.startsWith('"') && text.endsWith('"')) text = text.slice(1, -1);
            text = text.replace(/""/g, '"');
            if (isNaN(id)) return null;
            return { id, text, topic, comedian };
        } catch (e) {
            console.error("Could not parse CSV line:", line, e);
            return null;
        }
    }).filter(Boolean);
}

const sanitizeKey = (str) => str.replace(/[.$#[\]/]/g, '_');

// --- Reusable Modal Component ---
function Modal({ title, message, confirmText, onConfirm, onCancel, children }) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 text-center">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
                <p className="text-gray-600 mb-6">{message}</p>
                {children}
                <div className="flex justify-center gap-4 mt-6">
                    {onCancel && (<button onClick={onCancel} className="px-6 py-2 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300">Cancel</button>)}
                    {onConfirm && (<button onClick={onConfirm} className="px-6 py-2 bg-blue-500 text-white font-bold rounded-lg hover:bg-blue-600">{confirmText || 'OK'}</button>)}
                </div>
            </motion.div>
        </div>
    );
}

// --- Onboarding Component ---
function Onboarding({ onComplete, topics, comedians, userId }) {
    const [step, setStep] = useState(1);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [selectedComedians, setSelectedComedians] = useState([]);
    const [alert, setAlert] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const toggleSelection = (list, setList, item) => {
        setList(prevList => prevList.includes(item) ? prevList.filter(i => i !== item) : [...prevList, item]);
    };

    const handleNext = () => {
        if (selectedTopics.length < 5) {
            setAlert({ title: "Hold Up!", message: `Please select at least 5 topics to continue.` });
            return;
        }
        setStep(step + 1);
    };
    
    const handleDone = async () => {
        if (selectedComedians.length < 5) {
            setAlert({ title: "Almost There!", message: `Please select at least 5 comedians to finish.` });
            return;
        }
        setIsSaving(true);
        try {
            await onComplete({ topics: selectedTopics, comedians: selectedComedians });
        } catch (error) {
            console.error("Onboarding completion failed:", error);
            setAlert({ title: "Error", message: "Could not save your preferences. Please try again." });
            setIsSaving(false);
        }
    };

    const handleSkip = async () => {
        setIsSaving(true);
        try {
            await onComplete({ topics: [], comedians: [] });
        } catch (error) {
            console.error("Onboarding skip failed:", error);
            setAlert({ title: "Error", message: "Could not process your request. Please wait a moment and try again." });
            setIsSaving(false);
        }
    };

    const SelectionGrid = ({ title, items, selectedItems, onToggle }) => (
        <div className="text-center">
            <h2 className="text-2xl font-bold mb-2 text-gray-800">{title}</h2>
            <p className="text-gray-500 mb-6">Select at least 5. The more you pick, the better the jokes!</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {items.map(item => (
                    <button key={item} onClick={() => onToggle(item)} className={`p-3 rounded-lg font-semibold transition-all duration-200 text-sm ${selectedItems.includes(item) ? 'bg-blue-500 text-white shadow-md scale-105' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>{item}</button>
                ))}
            </div>
        </div>
    );

    return (
        <div className="w-full max-w-md mx-auto p-6 bg-white rounded-2xl shadow-xl">
            {alert && <Modal title={alert.title} message={alert.message} onConfirm={() => setAlert(null)} />}
            {step === 1 && (
                <div>
                    <SelectionGrid title="What topics make you laugh?" items={topics} selectedItems={selectedTopics} onToggle={(item) => toggleSelection(selectedTopics, setSelectedTopics, item)} />
                    <div className="mt-8 flex justify-between items-center">
                        <button onClick={handleSkip} className="text-gray-500 hover:text-gray-800 font-semibold disabled:opacity-50" disabled={isSaving || !userId}>
                            {isSaving ? '...' : 'Skip for now'}
                        </button>
                        <button onClick={handleNext} className="px-6 py-2 bg-blue-500 text-white font-bold rounded-lg shadow-md hover:bg-blue-600 disabled:bg-gray-400" disabled={!userId || isSaving || selectedTopics.length < 5}>Next</button>
                    </div>
                </div>
            )}
            {step === 2 && (
                <div>
                    <SelectionGrid title="Who are your favorite comedians?" items={comedians} selectedItems={selectedComedians} onToggle={(item) => toggleSelection(selectedComedians, setSelectedComedians, item)} />
                    <div className="mt-8 flex justify-between items-center">
                        <button onClick={() => setStep(1)} className="text-gray-500 hover:text-gray-800 font-semibold">Back</button>
                        <button onClick={handleDone} className="px-6 py-2 bg-green-500 text-white font-bold rounded-lg shadow-md hover:bg-green-600 disabled:bg-gray-400" disabled={isSaving || !userId || selectedComedians.length < 5}>
                            {isSaving ? 'Saving...' : 'Done!'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- Joke Card Component ---
const JokeCard = forwardRef(({ joke, onSwipe }, ref) => {
    const controls = useAnimation();
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const animateSwipe = (direction) => {
        triggerHapticFeedback();
        const exitX = direction === 'dislike' ? -300 : direction === 'like' ? 300 : 0;
        const exitY = direction === 'hilarious' ? -500 : 0;
        return controls.start({ x: exitX, y: exitY, opacity: 0, transition: { duration: 0.4 } });
    };

    useImperativeHandle(ref, () => ({ swipe: (direction) => { 
        animateSwipe(direction).then(() => {
            onSwipe(direction);
        }); 
    }}));

    const handleDragEnd = (event, info) => {
        let direction = null;
        if (info.offset.x < -100) direction = 'dislike';
        else if (info.offset.x > 100) direction = 'like';
        else if (info.offset.y < -100) direction = 'hilarious';

        if (direction) {
            animateSwipe(direction).then(() => {
                onSwipe(direction);
            });
        } else {
            controls.start({ x: 0, y: 0 });
        }
    };

    const rotate = useTransform(x, [-200, 200], [-30, 30]);
    const likeOpacity = useTransform(x, [0, 100], [0, 1]);
    const dislikeOpacity = useTransform(x, [0, -100], [0, 1]);
    const hilariousOpacity = useTransform(y, [0, -100], [0, 1]);

    return (
        <motion.div drag dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }} onDragEnd={handleDragEnd} style={{ x, y, rotate }} animate={controls} className="absolute w-full h-full cursor-grab active:cursor-grabbing">
            <div className="relative w-full h-full">
                <div className="absolute w-full h-full p-6 sm:p-8 bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100 border border-gray-200 rounded-2xl shadow-2xl flex flex-col">
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-xs font-semibold bg-white/50 text-blue-800 px-3 py-1 rounded-full">{joke.topic}</span>
                        <span className="text-xs font-semibold bg-white/50 text-purple-800 px-3 py-1 rounded-full">{joke.comedian}</span>
                    </div>
                    <div className="flex-grow flex items-center justify-center">
                        <p className="text-gray-800 text-xl sm:text-2xl font-medium leading-relaxed text-center">{joke.text}</p>
                    </div>
                </div>
                <motion.div style={{ opacity: dislikeOpacity }} className="absolute inset-0 rounded-2xl flex justify-center items-center pointer-events-none bg-red-500/50"><span className="text-8xl">👎</span></motion.div>
                <motion.div style={{ opacity: likeOpacity }} className="absolute inset-0 rounded-2xl flex justify-center items-center pointer-events-none bg-green-500/50"><span className="text-8xl">❤️</span></motion.div>
                <motion.div style={{ opacity: hilariousOpacity }} className="absolute inset-0 rounded-2xl flex justify-center items-center pointer-events-none bg-violet-500/50"><span className="text-8xl">😂</span></motion.div>
            </div>
        </motion.div>
    );
});

// --- Main Joke Feed Component ---
function JokeFeed({ userId, db, allJokes, onShowSaved, onShowSettings, streak, onStartOver }) {
    const [jokes, setJokes] = useState([]);
    const [interactions, setInteractions] = useState({});
    const [preferences, setPreferences] = useState({ topics: {}, comedians: {} });
    const [savedJokes, setSavedJokes] = useState([]);
    const [showConfetti, setShowConfetti] = useState(false);
    const [showCopiedMessage, setShowCopiedMessage] = useState(false);
    const topCardRef = useRef(null);

    useEffect(() => {
        if (!userId || !db) return;
        const unsubInteractions = onSnapshot(collection(db, `artifacts/${APP_ID}/users/${userId}/jokeInteractions`), (snapshot) => setInteractions(snapshot.docs.reduce((acc, doc) => ({...acc, [doc.id]: doc.data().interaction}), {})));
        const unsubPreferences = onSnapshot(doc(db, `artifacts/${APP_ID}/users/${userId}/preferences/${PREFERENCES_DOC_ID}`), (doc) => { if (doc.exists()) setPreferences(doc.data()); });
        const unsubSaved = onSnapshot(collection(db, `artifacts/${APP_ID}/users/${userId}/savedJokes`), (snapshot) => setSavedJokes(snapshot.docs.map(doc => doc.id)));
        return () => { unsubInteractions(); unsubPreferences(); unsubSaved(); };
    }, [userId, db]);

    const jokeQueue = useMemo(() => {
        if (!preferences.topics || !preferences.comedians || allJokes.length === 0) return [];
        const unseenJokes = allJokes.filter(joke => !interactions[joke.id]);
        const getScore = (item, type) => {
            const prefs = preferences[type]?.[sanitizeKey(item)] || {};
            return (prefs.like || 0) * 1 + (prefs.hilarious || 0) * 2 - (prefs.dislike || 0) * 1;
        };
        return unseenJokes.sort((a, b) => (getScore(b.topic, 'topics') + getScore(b.comedian, 'comedians')) - (getScore(a.topic, 'topics') + getScore(a.comedian, 'comedians')));
    }, [interactions, preferences, allJokes]);
    
    useEffect(() => { setJokes(jokeQueue.slice(0, 10)); }, [jokeQueue]);

    const handleSwipe = async (joke, direction) => {
        if (!userId || !db || !joke) return;
        if (direction === 'hilarious') {
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 2000);
        }
        setJokes(prev => prev.filter(j => j.id !== joke.id));
        const interactionRef = doc(db, `artifacts/${APP_ID}/users/${userId}/jokeInteractions`, String(joke.id));
        await setDoc(interactionRef, { interaction: direction, timestamp: serverTimestamp() });
        
        const prefRef = doc(db, `artifacts/${APP_ID}/users/${userId}/preferences/${PREFERENCES_DOC_ID}`);
        const topicPath = `topics.${sanitizeKey(joke.topic)}.${direction}`;
        const comedianPath = `comedians.${sanitizeKey(joke.comedian)}.${direction}`;
        const achievementPath = `achievements.${direction}Count`;
        const punPath = joke.topic === 'Puns' ? `achievements.punsRated` : null;
        
        const updates = {
            [topicPath]: increment(1),
            [comedianPath]: increment(1),
            [achievementPath]: increment(1)
        };
        if(punPath) updates[punPath] = increment(1);

        try { 
            await updateDoc(prefRef, updates); 
        } catch(e) { 
            console.error("Failed to update preferences. This can happen if the preferences document was deleted.", e);
        }
    };
    
    const handleSave = async (joke) => {
        if (!userId || !db || !joke) return;
        triggerHapticFeedback();
        const savedRef = doc(db, `artifacts/${APP_ID}/users/${userId}/savedJokes`, String(joke.id));
        const prefRef = doc(db, `artifacts/${APP_ID}/users/${userId}/preferences/${PREFERENCES_DOC_ID}`);

        try {
            if (savedJokes.includes(String(joke.id))) {
                await deleteDoc(savedRef);
                await updateDoc(prefRef, { 'achievements.savedCount': increment(-1) });
            } else {
                await setDoc(savedRef, joke);
                await updateDoc(prefRef, { 'achievements.savedCount': increment(1) });
            }
        } catch (e) {
            console.error("Failed to save/unsave joke.", e);
        }
    };

    const handleShare = async (joke) => {
        const shareText = `"${joke.text}" - ${joke.comedian}`;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Check out this joke!',
                    text: shareText,
                });
            } catch (error) {
                console.error('Error sharing:', error);
            }
        } else {
            // Fallback: copy to clipboard
            try {
                const textArea = document.createElement('textarea');
                textArea.value = shareText;
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                setShowCopiedMessage(true);
                setTimeout(() => setShowCopiedMessage(false), 2000);
            } catch (err) {
                console.error('Failed to copy: ', err);
            }
        }
    };
    
    const handleButtonSwipe = (direction) => { if (topCardRef.current) topCardRef.current.swipe(direction); };

    const currentJoke = jokes[0];
    const isSaved = currentJoke ? savedJokes.includes(String(currentJoke.id)) : false;

    return (
        <div className="w-full max-w-sm h-[90vh] sm:h-[650px] mx-auto flex flex-col items-center">
            <div className="w-full flex justify-between items-center mb-4 px-2">
                <button onClick={() => onShowSettings(true)} className="p-3 rounded-full bg-white/50 shadow-md hover:bg-white/80 transition-colors"><SettingsIcon className="w-6 h-6 text-gray-700"/></button>
                <div className="flex items-center gap-2">
                    <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Jokistaan</h1>
                    {streak > 1 && <div className="flex items-center gap-1 text-orange-500 font-bold"><FlameIcon className="w-6 h-6"/> {streak}</div>}
                </div>
                <button onClick={() => onShowSaved(true)} className="p-3 rounded-full bg-white/50 shadow-md hover:bg-white/80 transition-colors"><BookmarkIcon className="w-6 h-6 text-yellow-500" filled={true}/></button>
            </div>
            <div className="relative w-full flex-grow">
                {showConfetti && <Confetti />}
                <AnimatePresence>
                    {jokes.length > 0 ? (
                        jokes.slice(0, 2).reverse().map((joke, index) => {
                            const isTopCard = index === jokes.slice(0, 2).length - 1;
                            return (
                                <motion.div 
                                    key={joke.id} 
                                    className="absolute w-full h-full"
                                    initial={{ scale: 1 - (1 - index) * 0.05, y: (1 - index) * 20 }}
                                    animate={{ scale: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                    style={{ zIndex: index }}
                                    layout
                                >
                                    <JokeCard ref={isTopCard ? topCardRef : null} joke={joke} onSwipe={(direction) => handleSwipe(joke, direction)} />
                                </motion.div>
                            );
                        })
                    ) : (
                        <motion.div 
                            className="w-full h-full flex flex-col justify-center items-center bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100 border border-gray-200 rounded-2xl shadow-2xl p-6 text-center"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            <h3 className="text-2xl font-bold text-gray-800 mb-2">That's all for now!</h3>
                            <p className="text-gray-500 mb-8">You've seen all the jokes. What would you like to do next?</p>
                            <div className="flex flex-col gap-4 w-full">
                                <button 
                                    onClick={() => onShowSaved(true)} 
                                    className="w-full px-6 py-3 bg-blue-500 text-white font-bold rounded-lg shadow-md hover:bg-blue-600"
                                >
                                    Review Saved Jokes
                                </button>
                                <button 
                                    onClick={onStartOver} 
                                    className="w-full px-6 py-3 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300"
                                >
                                    Start Over (See All Jokes Again)
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
                {showCopiedMessage && (
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-lg text-sm"
                    >
                        Copied to clipboard!
                    </motion.div>
                )}
            </div>
            {jokes.length > 0 && (
                <div className="mt-6 w-full">
                    <div className="flex justify-around items-center w-full max-w-xs mx-auto">
                        <motion.button whileTap={{ scale: 0.9 }} onClick={() => handleButtonSwipe('dislike')} className="p-4 bg-white rounded-full shadow-lg" aria-label="Dislike joke"><span className="text-2xl">👎</span></motion.button>
                        <motion.button whileTap={{ scale: 0.9 }} onClick={() => handleShare(currentJoke)} className="p-3 bg-white rounded-full shadow-lg" aria-label="Share joke"><ShareIcon className="w-7 h-7 text-gray-500" /></motion.button>
                        <motion.button whileTap={{ scale: 0.9 }} onClick={() => handleSave(currentJoke)} className="p-3 bg-white rounded-full shadow-lg" aria-label="Save joke"><BookmarkIcon className={`w-7 h-7 text-yellow-500`} filled={isSaved} /></motion.button>
                        <motion.button whileTap={{ scale: 0.9 }} onClick={() => handleButtonSwipe('like')} className="p-4 bg-white rounded-full shadow-lg" aria-label="Like joke"><span className="text-2xl">❤️</span></motion.button>
                        <motion.button whileTap={{ scale: 0.9 }} onClick={() => handleButtonSwipe('hilarious')} className="p-4 bg-white rounded-full shadow-lg" aria-label="Hilarious joke"><span className="text-2xl">😂</span></motion.button>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- Saved Jokes Modal Component ---
function SavedJokesModal({ userId, db, onClose }) {
    const [savedJokes, setSavedJokes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        if (!userId || !db) return;
        const unsub = onSnapshot(collection(db, `artifacts/${APP_ID}/users/${userId}/savedJokes`), (snapshot) => {
            setSavedJokes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        });
        return () => unsub();
    }, [userId, db]);
    return (
        <Modal title="Saved Jokes" onCancel={onClose}>
            <div className="w-full max-h-[50vh] overflow-y-auto space-y-4 pr-2 -mr-2">
                {isLoading ? <p className="text-gray-500">Loading...</p> : savedJokes.length > 0 ? (
                    savedJokes.map(joke => (
                        <div key={joke.id} className="p-4 bg-gray-100 rounded-lg text-left">
                            <p className="text-gray-800">{joke.text}</p>
                            <div className="flex justify-end text-xs text-gray-500 mt-2"><span>{joke.topic} | {joke.comedian}</span></div>
                        </div>
                    ))
                ) : (<p className="text-gray-500 text-center py-8">You haven't saved any jokes yet!</p>)}
            </div>
        </Modal>
    );
}

// --- Settings Modal Component ---
function SettingsModal({ onClose, onReset, onShowAchievements, comedyProfile }) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl shadow-xl w-full max-w-sm flex flex-col p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Settings</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-3xl leading-none">&times;</button>
                </div>
                <div className="space-y-4">
                    <div className="p-4 bg-gray-100 rounded-lg text-left">
                        <h3 className="font-semibold text-gray-700 mb-2">Your Comedy Profile</h3>
                        <p className="text-sm text-gray-600"><strong>Top Topic:</strong> {comedyProfile.topTopic || 'N/A'}</p>
                        <p className="text-sm text-gray-600"><strong>Top Comedian:</strong> {comedyProfile.topComedian || 'N/A'}</p>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-gray-100 rounded-lg">
                        <span className="font-semibold text-gray-700">Achievements</span>
                        <button onClick={onShowAchievements} className="p-2 rounded-full bg-white/50 shadow-md hover:bg-white/80 transition-colors"><TrophyIcon className="w-6 h-6 text-yellow-500"/></button>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-gray-100 rounded-lg">
                        <div>
                            <p className="font-semibold text-gray-700">Reset Data</p>
                            <p className="text-xs text-gray-500">Clear all your preferences and saved jokes.</p>
                        </div>
                        <button onClick={onReset} className="px-4 py-2 text-sm bg-red-500 text-white font-bold rounded-lg hover:bg-red-600">Reset</button>
                    </div>
                </div>
                 <button onClick={onClose} className="mt-6 w-full px-6 py-2 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300">Back to Jokes</button>
            </motion.div>
        </div>
    );
}

// --- Achievements Modal ---
function AchievementsModal({ onClose, achievements }) {
    const achievementList = [
        { key: 'likeCount', title: 'Appreciator', goal: 25, label: 'Liked Jokes' },
        { key: 'dislikeCount', title: 'The Critic', goal: 50, label: 'Disliked Jokes' },
        { key: 'hilariousCount', title: 'ROFL', goal: 20, label: 'Hilarious Jokes' },
        { key: 'savedCount', title: 'Joke Hoarder', goal: 25, label: 'Saved Jokes' },
        { key: 'punsRated', title: 'Pun-isher', goal: 25, label: 'Puns Rated' }
    ];

    return (
        <Modal title="Achievements" onCancel={onClose}>
            <div className="w-full max-h-[50vh] overflow-y-auto space-y-4 pr-2 -mr-2">
                {achievementList.map(ach => {
                    const count = achievements[ach.key] || 0;
                    const progress = Math.min((count / ach.goal) * 100, 100);
                    const isComplete = count >= ach.goal;
                    return (
                        <div key={ach.key} className="p-4 bg-gray-100 rounded-lg">
                            <div className="flex items-center justify-between">
                                <TrophyIcon className={`w-8 h-8 ${isComplete ? 'text-yellow-500' : 'text-gray-400'}`} />
                                <div className="flex-1 mx-4">
                                    <p className="font-bold text-gray-800">{ach.title}</p>
                                    <p className="text-xs text-gray-500">{ach.label}</p>
                                </div>
                                <p className="font-semibold text-gray-700">{count} / {ach.goal}</p>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                                <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${progress}%` }}></div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
}

// --- Confetti Component ---
function Confetti() {
    const particles = useMemo(() => Array.from({ length: 30 }).map((_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: -20 - Math.random() * 50,
        rotation: Math.random() * 360,
        scale: Math.random() * 0.5 + 0.5,
        duration: Math.random() * 1 + 1.5,
        delay: Math.random() * 0.5,
        color: ['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#8b5cf6'][Math.floor(Math.random() * 5)]
    })), []);

    return (
        <div className="absolute inset-0 pointer-events-none z-50 overflow-hidden">
            {particles.map(p => (
                <motion.div
                    key={p.id}
                    className="absolute w-3 h-3 rounded-full"
                    style={{ left: `${p.x}%`, top: `${p.y}%`, backgroundColor: p.color, scale: p.scale }}
                    animate={{ y: '120vh', rotate: p.rotation + 180 }}
                    transition={{ duration: p.duration, delay: p.delay, ease: "linear" }}
                />
            ))}
        </div>
    );
}

// --- Main App Component ---
export default function App() {
    const [firebaseServices, setFirebaseServices] = useState(null);
    const [firebaseError, setFirebaseError] = useState(false);
    const [authError, setAuthError] = useState(null);
    const [jokeFetchError, setJokeFetchError] = useState(false);
    
    const [authStatus, setAuthStatus] = useState({ isReady: false, userId: null });
    const [allJokes, setAllJokes] = useState([]);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showSavedJokes, setShowSavedJokes] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showAchievementsModal, setShowAchievementsModal] = useState(false);
    const [resetModal, setResetModal] = useState(false);
    const [startOverModal, setStartOverModal] = useState(false);
    const [streak, setStreak] = useState(0);
    const [preferences, setPreferences] = useState(null);

    // Effect for initializing Firebase ONCE on component mount.
    useEffect(() => {
        const initTimer = setTimeout(() => {
            const localFirebaseConfig = {
                apiKey: "AIzaSyCQ-eMgFKPy8VTz1ygwkBnbZ4PsYRVuWg8",
                authDomain: "comicstaan-ec68f.firebaseapp.com",
                projectId: "comicstaan-ec68f",
                storageBucket: "comicstaan-ec68f.firebasestorage.app",
                messagingSenderId: "314961656365",
                appId: "1:314961656365:web:81626f6312fe63b54e96a4",
                measurementId: "G-VNHQL4QJHD"
            };

            let firebaseConfig = null;

            if (typeof __firebase_config !== 'undefined' && __firebase_config) {
                try {
                    firebaseConfig = JSON.parse(__firebase_config);
                    console.log("Using cloud-injected Firebase config.");
                } catch (e) {
                    console.error("Failed to parse cloud-injected Firebase config:", e);
                    setFirebaseError(true);
                    return;
                }
            } else if (localFirebaseConfig && localFirebaseConfig.apiKey) {
                firebaseConfig = localFirebaseConfig;
                console.log("Using local Firebase config for local dev.");
            } else {
                console.warn("Firebase config not found.");
                setFirebaseError(true);
                return; // Exit initialization to avoid further errors
            }

            try {
                if (firebaseConfig) {
                    const app = initializeApp(firebaseConfig);
                    const db = getFirestore(app);
                    const auth = getAuth(app);
                    setFirebaseServices({ db, auth });
                    setFirebaseError(false);
                } else {
                    setFirebaseError(true);
                }
            } catch (e) {
                console.error("Error initializing Firebase:", e);
                setFirebaseError(true);
            }
        }, 100);

        return () => clearTimeout(initTimer);
    }, []);

    // This effect ensures Tailwind CSS is loaded and configured for styling.
    useEffect(() => {
        if (!document.getElementById('tailwind-cdn')) {
            const script = document.createElement('script');
            script.id = 'tailwind-cdn';
            script.src = "https://cdn.tailwindcss.com";
            document.head.appendChild(script);
        }
        if (!document.getElementById('inter-font')) {
            const link = document.createElement('link');
            link.id = 'inter-font';
            link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
            link.rel = "stylesheet";
            document.head.appendChild(link);
        }
    }, []);

    const fetchJokes = async () => {
        setJokeFetchError(false);
        try {
            const response = await fetch('https://docs.google.com/spreadsheets/d/e/2PACX-1vR7C-3QYjUgtp8XAvQa2WuDdz5KJ_ZhPNYAxMUHBkGlFpWRqDKh-t0tc_2zkwOS1gEqZIiJLX51gxzi/pub?output=csv');
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const csvText = await response.text();
            setAllJokes(parseCSV(csvText));
        } catch (error) {
            console.error("Failed to fetch jokes:", error);
            setJokeFetchError(true);
        }
    };

    useEffect(() => {
        fetchJokes();
    }, []);

    // Auth handling effect, dependent on Firebase being ready.
    useEffect(() => {
        if (!firebaseServices) return;
        const { auth } = firebaseServices;

        const unsub = onAuthStateChanged(auth, (user) => {
            setAuthStatus({ isReady: true, userId: user ? user.uid : null });
        });

        (async () => {
            if (!auth.currentUser) {
                try {
                    const token = typeof __initial_auth_token !== 'undefined' ? __initial_auth_token : null;
                    if (token) await signInWithCustomToken(auth, token);
                    else await signInAnonymously(auth);
                } catch (error) {
                    console.error("Authentication failed:", error);
                    setAuthError("Could not sign in. Please check your connection and Firebase project settings for anonymous authentication.");
                    setAuthStatus({ isReady: true, userId: null });
                }
            }
        })();

        return () => unsub();
    }, [firebaseServices]);

    // User data fetching effect, runs when user ID changes.
    useEffect(() => {
        if (!authStatus.isReady || !firebaseServices) return;
        
        if (!authStatus.userId) {
            setShowOnboarding(true);
            setPreferences(null);
            return;
        }

        const { db } = firebaseServices;
        const userPrefRef = doc(db, `artifacts/${APP_ID}/users/${authStatus.userId}/preferences/${PREFERENCES_DOC_ID}`);
        
        const unsub = onSnapshot(userPrefRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setPreferences(data);
                setShowOnboarding(false);

                // Daily Streak Logic
                const today = new Date().toISOString().split('T')[0];
                const lastActive = data.lastActive;
                if (lastActive !== today) {
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    const yesterdayStr = yesterday.toISOString().split('T')[0];
                    const newStreak = lastActive === yesterdayStr ? (data.streak || 0) + 1 : 1;
                    setStreak(newStreak);
                    updateDoc(userPrefRef, { streak: newStreak, lastActive: today });
                } else {
                    setStreak(data.streak || 0);
                }
            } else {
                setShowOnboarding(true);
                setPreferences(null);
            }
        });

        return () => unsub();
    }, [authStatus.isReady, authStatus.userId, firebaseServices]);

    const { uniqueTopics, uniqueComedians } = useMemo(() => {
        if (allJokes.length === 0) return { uniqueTopics: [], uniqueComedians: [] };
        const topics = new Set(allJokes.map(joke => joke.topic));
        const comedians = new Set(allJokes.map(joke => joke.comedian));
        return { uniqueTopics: [...topics], uniqueComedians: [...comedians] };
    }, [allJokes]);

    const comedyProfile = useMemo(() => {
        if (!preferences) return { topTopic: null, topComedian: null };
        const findTopItem = (category) => {
            if (!preferences[category]) return null;
            return Object.entries(preferences[category]).sort(([, a], [, b]) => {
                const scoreA = (a.like || 0) + (a.hilarious || 0) * 2 - (a.dislike || 0);
                const scoreB = (b.like || 0) + (b.hilarious || 0) * 2 - (b.dislike || 0);
                return scoreB - scoreA;
            })[0]?.[0];
        };
        return { topTopic: findTopItem('topics'), topComedian: findTopItem('comedians') };
    }, [preferences]);

    const handleOnboardingComplete = async (selections) => {
        const { userId } = authStatus;
        const { db } = firebaseServices;
        if (!userId || !db) throw new Error("User not authenticated or database not ready.");

        const prefRef = doc(db, `artifacts/${APP_ID}/users/${userId}/preferences/${PREFERENCES_DOC_ID}`);
        const initialPrefs = { topics: {}, comedians: {}, achievements: { likeCount: 0, dislikeCount: 0, hilariousCount: 0, savedCount: 0, punsRated: 0 } };
        selections.topics.forEach(topic => {
            initialPrefs.topics[sanitizeKey(topic)] = { like: 1 };
        });
        selections.comedians.forEach(comedian => {
            initialPrefs.comedians[sanitizeKey(comedian)] = { like: 1 };
        });
        await setDoc(prefRef, initialPrefs);
        setShowOnboarding(false);
    };

    const handleResetRequest = () => {
        setShowSettingsModal(false);
        setResetModal(true);
    };

    const confirmReset = async () => {
        const { userId } = authStatus;
        const { db } = firebaseServices;
        if (!userId || !db) return;
        setResetModal(false);

        const collectionsToClear = ['jokeInteractions', 'savedJokes'];
        const batch = writeBatch(db);
        for (const coll of collectionsToClear) {
            const snapshot = await getDocs(collection(db, `artifacts/${APP_ID}/users/${userId}/${coll}`));
            snapshot.docs.forEach(doc => batch.delete(doc.ref));
        }
        batch.delete(doc(db, `artifacts/${APP_ID}/users/${userId}/preferences/${PREFERENCES_DOC_ID}`));
        await batch.commit();
        
        setPreferences(null); 
        setShowOnboarding(true);
    };

    const handleStartOverRequest = () => {
        setStartOverModal(true);
    };

    const confirmStartOver = async () => {
        const { userId } = authStatus;
        const { db } = firebaseServices;
        if (!userId || !db) return;
        setStartOverModal(false);

        const interactionsCollectionRef = collection(db, `artifacts/${APP_ID}/users/${userId}/jokeInteractions`);
        const snapshot = await getDocs(interactionsCollectionRef);
        const batch = writeBatch(db);
        snapshot.docs.forEach(doc => batch.delete(doc.ref));
        await batch.commit();
    };

    const isLoading = !firebaseServices || !authStatus.isReady || (allJokes.length === 0 && !jokeFetchError);

    if (firebaseError) {
        return (
            <div className="w-full h-screen flex justify-center items-center bg-red-100 p-4">
                <div className="text-center text-red-800 bg-white p-8 rounded-lg shadow-lg">
                    <h2 className="text-2xl font-bold mb-4">Configuration Error</h2>
                    <p>Firebase is not configured correctly. The app cannot load.</p>
                </div>
            </div>
        )
    }

    if (authError) {
        return (
            <div className="w-full h-screen flex justify-center items-center bg-red-100 p-4">
                <div className="text-center text-red-800 bg-white p-8 rounded-lg shadow-lg">
                    <h2 className="text-2xl font-bold mb-4">Authentication Error</h2>
                    <p>{authError}</p>
                    <p className="text-sm mt-4 text-gray-600">Please ensure Anonymous Authentication is enabled in your Firebase project and that <strong>localhost</strong> is an authorized domain.</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="w-full h-screen flex justify-center items-center bg-gradient-to-br from-blue-50 to-pink-100">
                <p className="text-xl font-semibold text-gray-600 animate-pulse">Fetching fresh jokes...</p>
            </div>
        );
    }

    if (jokeFetchError) {
        return (
            <div className="w-full h-screen flex justify-center items-center bg-red-100 p-4">
                <div className="text-center text-red-800 bg-white p-8 rounded-lg shadow-lg">
                    <h2 className="text-2xl font-bold mb-4">Oops!</h2>
                    <p>We couldn't load the jokes. Please check your internet connection and try again.</p>
                    <button onClick={fetchJokes} className="mt-6 px-6 py-2 bg-red-500 text-white font-bold rounded-lg hover:bg-red-600">Retry</button>
                </div>
            </div>
        );
    }

    return (
        <main className="w-full min-h-screen bg-gradient-to-br from-blue-50 to-pink-100 font-sans flex flex-col justify-center items-center p-2 sm:p-4 relative overflow-hidden">
            <div className="absolute top-2 left-2 bg-white/50 p-2 rounded-lg shadow-md text-xs text-gray-600 z-10">
                User ID: {authStatus.userId ? `${authStatus.userId.substring(0, 8)}...` : 'N/A'}
            </div>
            {showOnboarding ? (
                <Onboarding 
                    onComplete={handleOnboardingComplete} 
                    topics={uniqueTopics} 
                    comedians={uniqueComedians}
                    userId={authStatus.userId}
                />
            ) : (
                <JokeFeed 
                    userId={authStatus.userId} 
                    db={firebaseServices.db} 
                    allJokes={allJokes} 
                    onShowSaved={() => setShowSavedJokes(true)} 
                    onShowSettings={() => setShowSettingsModal(true)} 
                    streak={streak} 
                    onStartOver={handleStartOverRequest}
                />
            )}
            {showSavedJokes && (<SavedJokesModal userId={authStatus.userId} db={firebaseServices.db} onClose={() => setShowSavedJokes(false)} />)}
            {showSettingsModal && (
                <SettingsModal 
                    onClose={() => setShowSettingsModal(false)}
                    onReset={handleResetRequest}
                    onShowAchievements={() => { setShowSettingsModal(false); setShowAchievementsModal(true); }}
                    comedyProfile={comedyProfile}
                />
            )}
            {showAchievementsModal && (
                <AchievementsModal onClose={() => setShowAchievementsModal(false)} achievements={preferences?.achievements || {}} />
            )}
            {resetModal && (
                <Modal title="Reset Everything?" message="This will clear all your likes, dislikes, and saved jokes. Are you sure you want to start over?" confirmText="Yes, Reset" onConfirm={confirmReset} onCancel={() => setResetModal(false)} />
            )}
            {startOverModal && (
                <Modal 
                    title="Start Over?" 
                    message="This will reset your swipe history and show you all the jokes again. Your preferences and saved jokes will not be affected." 
                    confirmText="Yes, Start Over" 
                    onConfirm={confirmStartOver} 
                    onCancel={() => setStartOverModal(false)} 
                />
            )}
        </main>
    );
}
